import React, { useEffect } from "react";
import { Modal, Typography, Form, DatePicker, Upload, Button, Radio, Table, Input } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import PrimaryButton from "../Forms/PrimaryButton";
import { formRules, orderStatus } from "../../constant";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { roundDecimal, showAPC } from "../../utils";

const { Title } = Typography;

export default function DispatchModal({ open, onClose, handleSubmit, title = "Update to Dispatched", loading, orderData }) {
  const [form] = Form.useForm();
  const { showPopup } = useGlobalModal();

  const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

  const dispatchItems = orderData.items?.filter((item) => item.status !== orderStatus.dispatched) || [];

  const getBalances = (record) => {
    const total_cases = Number(record.update_total_cases || record.cases || 0);
    const total_qty = Number(record.update_total_meter || record.quantity_meters || 0);

    const past_dispatched_cases = Number(record.total_dispatch_cases || 0);
    const past_dispatched_qty = Number(record.total_dispatch_mtrs || 0);

    const balance_cases = Number(record.balance_cases_no || total_cases);
    const balance_meters = Number(record.balance_qty || total_qty);

    return {
      total_cases,
      total_qty,
      balance_cases,
      balance_meters,
    };
  };

  const resetAllStatus = () => {
    const currentItems = form.getFieldValue("items") || {};

    const items = {};
    const order_cases = {};
    const order_meters = {};
    const balance_cases = {};
    const balance_meters = {};

    const fields = [];

    for (const record of dispatchItems) {
      const id = String(record.order_item_id);

      const { total_cases, total_qty, balance_cases: recordBalanceCases, balance_meters: recordBalanceMeters } = getBalances(record);

      if (currentItems[id]) {
        fields.push(["order_cases", id], ["order_meters", id], ["balance_cases", id], ["balance_meters", id]);
      }

      items[id] = null;
      order_cases[id] = total_cases;
      order_meters[id] = roundDecimal(total_qty);
      balance_cases[id] = recordBalanceCases;
      balance_meters[id] = roundDecimal(recordBalanceMeters);
    }

    form.setFieldsValue({
      items,
      order_cases,
      order_meters,
      balance_cases,
      balance_meters,
    });

    if (fields.length) {
      form.validateFields(fields).catch(() => {});
    }
  };

  const dispatchColumns = [
    {
      title: "Quality Name",
      dataIndex: "short_name",
      width: 120,
      ellipsis: true,
      onCell: () => ({ style: { verticalAlign: "top", paddingTop: "16px" } }),
    },
    {
      title: "Shade",
      dataIndex: "shade_name",
      ellipsis: true,
      width: 80,
      onCell: () => ({ style: { verticalAlign: "top", paddingTop: "16px" } }),
      render: (value, item) => value + showAPC(item.apc_no) || "-",
    },
    {
      title: "Ordered Cases",
      width: 140,
      render: (_, record) => {
        return (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const status = getFieldValue(["items", String(record.order_item_id)]);
              const isDispatched = status === orderStatus.dispatched;

              return (
                <Form.Item
                  name={["order_cases", String(record.order_item_id)]}
                  rules={[
                    { required: !!status, message: "Required" },
                    {
                      validator(_, value) {
                        if (value === undefined || value === "") return Promise.resolve();

                        const num = Number(value);

                        if (num <= 0 || num % 1 !== 0) return Promise.reject(new Error("Invalid Entry"));

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input type="number" disabled={isDispatched || !status} className="no-spinner" />
                </Form.Item>
              );
            }}
          </Form.Item>
        );
      },
    },
    {
      title: "Ordered Meters",
      width: 150,
      render: (_, record) => {
        return (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const status = getFieldValue(["items", String(record.order_item_id)]);
              const isDispatched = status === orderStatus.dispatched;

              return (
                <Form.Item
                  name={["order_meters", String(record.order_item_id)]}
                  rules={[
                    { required: !!status, message: "Required" },
                    {
                      validator(_, value) {
                        if (value === undefined || value === "") return Promise.resolve();

                        const num = Number(value);

                        if (num < 1) return Promise.reject(new Error("Invalid Entry"));

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input type="number" disabled={isDispatched || !status} className="no-spinner" />
                </Form.Item>
              );
            }}
          </Form.Item>
        );
      },
    },
    {
      title: "Balance Cases",
      width: 140,
      render: (_, record) => (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const status = getFieldValue(["items", String(record.order_item_id)]);
            const isDispatched = status === orderStatus.dispatched;

            return (
              <Form.Item
                name={["balance_cases", String(record.order_item_id)]}
                dependencies={[["order_cases", String(record.order_item_id)]]}
                rules={[
                  { required: !!status, message: "Required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (isDispatched || !status) return Promise.resolve();

                      const orderCases = Number(getFieldValue(["order_cases", String(record.order_item_id)]) || 0);

                      if (value === undefined || value === "") return Promise.resolve();

                      const num = Number(value);

                      if (num <= 0 || num % 1 !== 0 || num > orderCases) return Promise.reject(new Error("Invalid Entry"));

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type="number" disabled={isDispatched || !status} className="no-spinner" />
              </Form.Item>
            );
          }}
        </Form.Item>
      ),
    },
    {
      title: "Balance Meters",
      width: 150,
      render: (_, record) => (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const status = getFieldValue(["items", String(record.order_item_id)]);
            const isDispatched = status === orderStatus.dispatched;

            return (
              <Form.Item
                name={["balance_meters", String(record.order_item_id)]}
                dependencies={[["order_meters", String(record.order_item_id)]]}
                rules={[
                  { required: !!status, message: "Required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (isDispatched || !status) return Promise.resolve();

                      const orderMeters = Number(getFieldValue(["order_meters", String(record.order_item_id)]) || 0);

                      if (value === undefined || value === "") return Promise.resolve();

                      const num = Number(value);

                      if (num < 1 || num > orderMeters) return Promise.reject(new Error("Invalid Entry"));

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type="number" disabled={isDispatched || !status} className="no-spinner" />
              </Form.Item>
            );
          }}
        </Form.Item>
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Update Status</span>
          <Button type="text" danger onClick={resetAllStatus} className="text-sm p-0">
            Reset
          </Button>
        </div>
      ),
      width: 260,
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        const isDispatched = record.status === orderStatus.dispatched;
        const isPartDispatched = record.status === orderStatus.parts_dispatched;

        return (
          <Form.Item name={["items", String(record.order_item_id)]} className="mb-0 whitespace-nowrap suggested-wrapper">
            <Radio.Group
              onChange={(e) => {
                const val = e.target.value;
                const { total_cases, total_qty, balance_cases: record_balance_cases, balance_meters: record_balance_meters } = getBalances(record);

                form.setFieldValue(["items", String(record.order_item_id)], val);

                if (val === orderStatus.dispatched) {
                  form.setFieldsValue({
                    order_cases: {
                      [record.order_item_id]: total_cases,
                    },
                    order_meters: {
                      [record.order_item_id]: roundDecimal(total_qty),
                    },
                    balance_cases: {
                      [record.order_item_id]: 0,
                    },
                    balance_meters: {
                      [record.order_item_id]: 0,
                    },
                  });
                }

                if (val === orderStatus.parts_dispatched) {
                  form.setFieldsValue({
                    order_cases: {
                      [record.order_item_id]: total_cases,
                    },
                    order_meters: {
                      [record.order_item_id]: roundDecimal(total_qty),
                    },
                    balance_cases: {
                      [record.order_item_id]: record_balance_cases,
                    },
                    balance_meters: {
                      [record.order_item_id]: roundDecimal(record_balance_meters),
                    },
                  });
                }
              }}
            >
              <Radio value={orderStatus.dispatched} className={isDispatched ? "suggested-radio" : ""}>
                Dispatched
              </Radio>
              <Radio value={orderStatus.parts_dispatched} className={isPartDispatched ? "suggested-radio" : ""}>
                Part Dispatched
              </Radio>
            </Radio.Group>
          </Form.Item>
        );
      },
    },
  ];

  const orderDataStr = JSON.stringify(orderData);

  useEffect(() => {
    const order_cases = {};
    const order_meters = {};
    const balance_cases = {};
    const balance_meters = {};

    const fieldsToValidate = [];

    (dispatchItems || []).forEach((record) => {
      const id = String(record.order_item_id);

      const { total_cases, total_qty, balance_cases: recordBalanceCases, balance_meters: recordBalanceMeters } = getBalances(record);

      const status = form.getFieldValue(["items", id]);

      order_cases[id] = total_cases;
      order_meters[id] = roundDecimal(total_qty);

      if (status === orderStatus.dispatched) {
        balance_cases[id] = 0;
        balance_meters[id] = 0;
      } else {
        balance_cases[id] = recordBalanceCases;
        balance_meters[id] = roundDecimal(recordBalanceMeters);
      }

      if (status) {
        fieldsToValidate.push(["order_cases", id], ["order_meters", id], ["balance_cases", id], ["balance_meters", id]);
      }
    });

    form.setFieldsValue({
      order_cases,
      order_meters,
      balance_cases,
      balance_meters,
    });

    if (fieldsToValidate.length) {
      form.validateFields(fieldsToValidate).catch(() => {});
    }
  }, [open, orderDataStr]);

  return (
    <Modal className="form-css custom-modal" open={open} footer={null} onCancel={onClose} centered width={550}>
      <Title level={2} className="font-abhaya mb-2">
        {title}
      </Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => handleSubmit(values, form)}
        initialValues={{
          dispatch_date: dayjs(),
        }}
      >
        <Title level={5} className="mb-4">
          Please add dispatch details
        </Title>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Form.Item label="Dispatch Date" name="dispatch_date" rules={formRules.required} className="mb-0 md:col-span-2">
            <DatePicker
              className="h-10 w-full"
              disabledDate={(current) => {
                return (current && current.isBefore(dayjs(orderData.created_at), "day")) || current.isAfter(dayjs().endOf("day"));
              }}
            />
          </Form.Item>

          <Form.Item
            label="Upload Invoice"
            name="invoice"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={formRules.required}
            className="mb-0"
          >
            <Upload
              accept=".pdf"
              maxCount={1}
              beforeUpload={(file) => {
                if (!file.name.endsWith(".pdf") && !file.name.endsWith(".PDF")) {
                  showPopup({
                    content: "Please upload only PDF files.",
                    className: "custom-success-modal",
                  });
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
            >
              <Button className="h-10 w-full" icon={<UploadOutlined />}>
                Upload Invoice Details
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="Upload Transport Details"
            name="transport"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={formRules.required}
            className="mb-0"
          >
            <Upload
              accept=".pdf"
              maxCount={1}
              beforeUpload={(file) => {
                if (!file.name.endsWith(".pdf") && !file.name.endsWith(".PDF")) {
                  showPopup({
                    content: "Please upload only PDF files.",
                    className: "custom-success-modal",
                  });
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
            >
              <Button className="h-10 w-full" icon={<UploadOutlined />}>
                Upload Transport Details
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <Title level={5} className="mt-6 mb-4">
          Select items to dispatch
        </Title>

        <Table
          rowKey="order_item_id"
          columns={dispatchColumns}
          dataSource={dispatchItems}
          pagination={false}
          size="middle"
          className="mb-3"
          rootClassName="transparent-table"
          scroll={{ x: "max-content" }}
        />

        <div className="flex justify-center mt-6">
          <PrimaryButton text="Update Status" loading={loading} type="submit" className="me-[30px]" />
        </div>
      </Form>
    </Modal>
  );
}
